import { useSyncExternalStore } from 'react';
import findLast from 'lodash/findLast';
import { DateTime } from 'luxon';
import { z, ZodError } from 'zod';

import type { PaymentMethodType } from '@autopass-web/types';

// Here is the valid error retryReason which registered. the format is {payment_method}:{reason}
const submitRecordSchema = z
  .object({
    paymentMethod: z.string(),
    reason: z.string().nullable(),
  })
  .required({
    paymentMethod: true,
  });

type InvoiceId = string;

type SubmitRecord = z.infer<typeof submitRecordSchema>;

const submitRecords = submitRecordSchema.array();

const invoiceInfo = z.object({
  createdAt: z.string().datetime({ offset: true }),
  records: submitRecords,
});

type InvoiceInfo = z.infer<typeof invoiceInfo>;

const submitInfoSchema = z.record(z.string(), invoiceInfo);

type SubmitInfo = z.infer<typeof submitInfoSchema>;

/** store the submit error and payment type for persist client side storage, which implement with LocalStorage. */
class SubmitInfoStorage {
  #KEY = '#submit_info';
  #storage: Storage | undefined;
  #listener?: () => void | undefined;

  constructor() {
    try {
      // test is localstorage working or not, if not. use the polyfill memory storage (just pure object in closure)
      const TEST_KEY = '__TEST__';
      window.localStorage.setItem(TEST_KEY, '');
      window.localStorage.removeItem(TEST_KEY);

      this.#storage = window.localStorage;
    } catch {
      this.#storage = this.#createStoragePolyfill();
    }

    // validate the storage data and cleanup outdated invoice data
    const info = this.#getInfo();

    for (const key in info) {
      const invoice = info[key];
      const diffDays = -DateTime.fromISO(invoice.createdAt).diffNow('day').days;

      const INVOICE_INFO_TIMEOUT_DAYS = 1;
      if (diffDays > INVOICE_INFO_TIMEOUT_DAYS) {
        delete info[key];
      }
    }

    this.#setInfo(info);
  }

  #createStoragePolyfill = () => {
    const obj = {};
    return {
      getItem: (key) => obj?.[key],
      setItem: (key, item) => {
        obj[key] = item;
      },
    } as Storage;
  };

  #createEmptySubmitInfo = () => ({}) as SubmitInfo;

  #createEmptyInvoiceInto = (): InvoiceInfo => ({
    createdAt: DateTime.now().toISO(),
    records: [],
  });

  #getInfo = () => {
    try {
      if (!this.#storage) throw new Error();
      const submitInfoText = this.#storage.getItem(this.#KEY);
      // parse the data from storage every time, make sure all data is schema matched
      const submitInfo: SubmitInfo = JSON.parse(submitInfoText);

      submitInfoSchema.parse(submitInfo);

      return submitInfo;
    } catch {
      console.trace();
      return this.#createEmptySubmitInfo();
    }
  };

  #setInfo = (info: SubmitInfo) => {
    try {
      submitInfoSchema.parse(info);

      const infoString = JSON.stringify(info);

      this.#storage.setItem(this.#KEY, infoString);

      // trigger React rerender
      this.#listener?.();
    } catch (err) {
      console.trace();
      if (err instanceof ZodError) {
        console.error(err.message, info);
      } else {
        console.warn(err.message);
      }
    }
  };

  addRecord = (
    invoiceId: InvoiceId,
    { paymentMethod }: { paymentMethod: string },
  ) => {
    const info = this.#getInfo();

    const invoice = info?.[invoiceId] ?? this.#createEmptyInvoiceInto();

    const newRecord: SubmitRecord = { paymentMethod, reason: null };

    // prevent duplicated reason record
    const isRecordDuplicated = invoice.records.some(
      ({ reason, paymentMethod }) =>
        paymentMethod === newRecord.paymentMethod &&
        reason === newRecord.reason,
    );

    if (isRecordDuplicated) return;

    invoice.records.push(newRecord);

    info[invoiceId] = invoice;

    this.#setInfo(info);
  };

  cleanRecords = (invoiceId: InvoiceId) => {
    const info = this.#getInfo();

    delete info[invoiceId];

    this.#setInfo(info);
  };

  cleanupRecordsReason(invoiceId: InvoiceId) {
    console.log('cleanupRecordsReason');

    const info = this.#getInfo();

    const invoiceInfo = info?.[invoiceId];
    if (!invoiceInfo) {
      return;
    }

    const records: SubmitRecord[] = invoiceInfo.records;

    for (const record of records) {
      record.reason = null;
    }

    this.#setInfo(info);
  }

  updateLatestInvoiceRecord(
    invoiceId: InvoiceId,
    { reason, paymentMethod }: Partial<SubmitRecord>,
  ) {
    const info = this.#getInfo();

    const invoiceInfo = info?.[invoiceId] ?? this.#createEmptyInvoiceInto();

    const records: SubmitRecord[] = invoiceInfo.records;

    const targetRecord = findLast(
      records,
      (record) => record.paymentMethod === paymentMethod,
    );

    if (targetRecord) {
      targetRecord.reason = reason;
    } else if (!!reason && !!paymentMethod) {
      // if record missing, insert the new record instead
      records.push({
        reason,
        paymentMethod,
      });
    }

    info[invoiceId] = invoiceInfo;

    this.#setInfo(info);
  }

  isInvoiceHasRecords = (invoiceId: InvoiceId) => {
    const info = this.#getInfo();

    const records = info?.[invoiceId].records;

    return !!records && records.length > 0;
  };

  getInvoiceRecords = (invoiceId: InvoiceId) => {
    const info = this.#getInfo();
    const records = info?.[invoiceId]?.records;

    return records;
  };

  subscribe = (cb: () => void) => {
    this.#listener = cb;

    this.#listener();
    return () => {
      this.#listener = undefined;
    };
  };

  getSnapshot = () => {
    return this;
  };

  delete = () => {
    window.localStorage.removeItem(this.#KEY);
  };
}

export const submitInfoStorage = new SubmitInfoStorage();

const useSubmitRecords = (invoiceId: InvoiceId) => {
  const submitInfo = useSyncExternalStore(
    submitInfoStorage.subscribe,
    submitInfoStorage.getSnapshot,
  );
  const records = submitInfo.getInvoiceRecords(invoiceId) ?? [];

  return {
    records,
    addRecords: (paymentMethod: PaymentMethodType) =>
      submitInfo.addRecord(invoiceId, { paymentMethod }),
    cleanRecords: () => submitInfo.cleanRecords(invoiceId),
  };
};

export { useSubmitRecords };
